import { CheckIcon } from "lucide-react";
import { useCallback } from "react";
import { cn } from "../../utils";
import { useDropdownContext } from "../Dropdown";
import { TruncatedContent } from "../TruncatedContent";
import { useSelectContext } from "./context";
import type { SelectOption as SelectOptionType } from "./types";

type SelectItemProps = {
  option: SelectOptionType;
  index: number;
};

export const SelectItem = ({ option, index }: SelectItemProps) => {
  const { value, multiple, onRenderOption, onSelect, contentId } =
    useSelectContext();
  const { navigationRef, getItemProps, activeIndex } = useDropdownContext();

  const optionId = `${contentId}-option-${index}`;

  const isSelected = multiple
    ? Array.isArray(value) && value.includes(option.id)
    : value === option.id;

  const isActive = activeIndex === index;

  const handleClick = useCallback(() => {
    if (option.disabled) {
      return;
    }
    onSelect(option.id);
  }, [onSelect, option.id, option.disabled]);

  return (
    <button
      {...(option.dataAttributes || {})}
      id={optionId}
      role="option"
      aria-selected={isSelected}
      aria-disabled={option.disabled}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-lg p-2 outline-none text-start text-sm",
        !option.disabled &&
          "hover:bg-primary-500/5 focus:bg-primary-500/5 active:bg-primary-500/10",
        option.disabled && "text-neutral-200 cursor-not-allowed",
        option.className,
      )}
      disabled={option.disabled}
      ref={navigationRef(index)}
      {...getItemProps?.()}
      onClick={handleClick}
      tabIndex={isActive ? 0 : -1}
    >
      {onRenderOption ? (
        onRenderOption(option)
      ) : (
        <>
          {option.icon && <div className="flex-shrink-0">{option.icon}</div>}
          <TruncatedContent className="whitespace-nowrap">
            {option.text}
          </TruncatedContent>
          {option.description && (
            <TruncatedContent className="whitespace-nowrap text-neutral-500/50">
              {option.description}
            </TruncatedContent>
          )}
        </>
      )}

      {multiple ? (
        <span
          aria-hidden
          className={cn(
            "ms-auto pointer-events-none transition-opacity shrink-0",
            isSelected ? "opacity-100" : "opacity-0",
          )}
        >
          <CheckIcon size="1rem" />
        </span>
      ) : (
        isSelected && (
          <CheckIcon
            className="ms-auto pointer-events-none shrink-0"
            size="1rem"
          />
        )
      )}
    </button>
  );
};
